/**
 * `/result/$attemptId` — quiz score + per-question review (SCH Milestone A).
 *
 * Reads `learning.attempts.get` for the headline (score %, points, pass /
 * fail) and `learning.attempts.getReview` for the per-question breakdown
 * (the learner's own answer mapped to option labels, correct / incorrect
 * verdicts, the now-revealed correct options + explanation on misses, and
 * an hourglass "Pending review" state for manually-graded kinds that an
 * instructor hasn't scored yet).
 *
 * While `status === 'submitted'` (not `'graded'`) — i.e. manual items are
 * still outstanding — an info banner warns that the score may change.
 *
 * SCH-AI-2b adds pacing presentation over the review DTO's `elapsedMs`
 * (post-submit wire data, already fetched): a per-question time chip on
 * each review row and a "Slowest: Q{n} ({s}s)" line in the score hero.
 * Client-side formatting only — no new queries.
 *
 * SCH-AI-3 adds the AI-mentor tier (plan Slice 3 + §6): a `<RecapCard>`
 * right under the score hero (post-submit only) and an `<ExplainMistake>`
 * entry on every incorrect review row, BELOW the static author explanation
 * — which always stays visible (the zero-LLM tier is untouched by quota or
 * availability).
 *
 * The data-viz layer (hand-rolled SVG/div, zero chart libs): the score hero
 * renders a `<DonutChart>` verdict ring (correct / incorrect / pending /
 * ungraded — score-ring fallback while the review loads), the review section
 * opens with a `<ColumnChart>` time-per-question strip whose columns
 * deep-link to their review rows, and a `<SkillBreakdownCard>` (BarList over
 * skillTags × points) sits between the recap and the review — the Bluebook
 * "Knowledge and Skills" pattern. All client-side reshaping of the same two
 * queries; no new fetches.
 *
 * The Try-again / Back-to-course action row is a STICKY footer (the quiz
 * runner's footer treatment) — the review averages ~30 cards, so a static
 * bottom row meant scrolling the whole thing to get back to the course.
 *
 * Question kinds are inlined as string checks (mirroring the learning
 * router's vocabulary) because this SPA must never import `@mangrove/core`.
 */
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ColumnChart,
  DonutChart,
  EmptyState,
  MathText,
  Separator,
  Skeleton,
  cn,
} from '@mangrove/ui';
import {
  Calendar,
  CheckCircle2,
  FileQuestion,
  Hourglass,
  Info,
  Lightbulb,
  MessageCircle,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';
import { LearnShell } from '../components/shell.js';
import { RecapCard } from '../components/result/recap-card.js';
import { ExplainMistake } from '../components/result/explain-mistake.js';
import { SkillBreakdownCard } from '../components/result/skill-breakdown-card.js';
import { MentorDock } from '../components/mentor/mentor-dock.js';

export const Route = createFileRoute('/result/$attemptId')({
  component: ResultPage,
});

// ---------------------------------------------------------------------------
// Types (derived from the api contract — no runtime backend imports)
// ---------------------------------------------------------------------------

type AttemptsOutputs = inferRouterOutputs<AppRouter>['learning']['attempts'];
type Attempt = NonNullable<AttemptsOutputs['get']>;
type Review = NonNullable<AttemptsOutputs['getReview']>;
type ReviewAnswer = Review['answers'][number];

/** Kinds an instructor grades by hand — no verdict until `gradedKind` is set. */
const MANUAL_KINDS: ReadonlySet<string> = new Set(['short_answer', 'essay']);

/**
 * The image attached to a review answer's prompt (`promptImageObjectId`) or one
 * of its options (`imageObjectId`), read DEFENSIVELY off the DTO. P-SCH-* adds
 * these fields to the learner review DTO (`AttemptReviewAnswerDto` + its
 * `options`) — until the regenerated `@mangrove/api-contract` rollup lands they
 * aren't in the inferred type (and a stale api omits them at runtime), so we
 * probe by name and accept only a non-empty string. Returns the `file_objects`
 * id, or null when there's no image.
 */
function imageObjectIdOf(
  carrier: unknown,
  field: 'promptImageObjectId' | 'imageObjectId',
): string | null {
  if (carrier && typeof carrier === 'object' && field in carrier) {
    const raw = (carrier as Record<string, unknown>)[field];
    if (typeof raw === 'string' && raw !== '') return raw;
  }
  return null;
}

/**
 * `<QuestionImage>` — render an image stored in `file_objects` inside the review
 * (the prompt figure, or an option's image). Mirrors the runner's component:
 * bytes are base64 in Postgres (ADR-0090), read through the learner-readable
 * `learning.attempts.getQuestionImage` proc (allowlisted `learning.attempts.`
 * prefix) and shown as a `data:` URL — the Drive preview pattern. Defensive:
 * the proc is accessed by name (so a not-yet-shipped proc can't break
 * typecheck), and a missing id / load failure renders NOTHING — the text via
 * <MathText> is always the source of truth.
 */
function QuestionImage({
  objectId,
  alt,
  className,
}: {
  objectId: string | null;
  alt: string;
  className?: string;
}): React.JSX.Element | null {
  const attempts = trpc.learning.attempts as unknown as {
    getQuestionImage?: {
      useQuery: (
        input: { objectId: string },
        opts: { enabled: boolean; staleTime: number },
      ) => { data?: { mediaType: string | null; dataBase64: string } };
    };
  };
  const query = attempts.getQuestionImage?.useQuery(
    { objectId: objectId ?? '' },
    { enabled: objectId != null, staleTime: Infinity },
  );
  if (objectId == null || !query?.data) return null;
  const { mediaType, dataBase64 } = query.data;
  return (
    <img
      src={`data:${mediaType ?? 'image/png'};base64,${dataBase64}`}
      alt={alt}
      className={cn(
        'max-h-80 max-w-full rounded-md border bg-background object-contain',
        className,
      )}
    />
  );
}

type Verdict = 'correct' | 'incorrect' | 'pending' | 'ungraded';

function verdictOf(a: ReviewAnswer): Verdict {
  if (MANUAL_KINDS.has(a.kind) && a.gradedKind == null) return 'pending';
  if (a.isCorrect === true) return 'correct';
  if (a.isCorrect === false) return 'incorrect';
  return 'ungraded';
}

/** "75%" / "66.7%" — at most one decimal, no trailing ".0". */
function formatScorePercent(p: number): string {
  return `${Math.round(p * 10) / 10}%`;
}

/** One option as the review lists render it — its label (may be LaTeX) + any image. */
interface OptionView {
  label: string;
  imageObjectId: string | null;
}

/**
 * The selected options for a choice answer — label (may contain LaTeX, rendered
 * per-label via <MathText>) + any attached image — or null for free-text /
 * numeric / unanswered.
 */
function learnerOptionLabels(a: ReviewAnswer): OptionView[] | null {
  if (a.selectedOptionIds && a.selectedOptionIds.length > 0) {
    const byId = new Map(a.options.map((o) => [o.id, o]));
    return a.selectedOptionIds.map((id) => {
      const o = byId.get(id);
      return o
        ? { label: o.label, imageObjectId: imageObjectIdOf(o, 'imageObjectId') }
        : { label: 'Unknown option', imageObjectId: null };
    });
  }
  return null;
}

/** The learner's free-text / numeric answer as plain text, or null. */
function formatLearnerText(a: ReviewAnswer): string | null {
  if (a.answerText != null && a.answerText.trim() !== '') return a.answerText;
  if (a.answerNumeric != null) return String(a.answerNumeric);
  return null;
}

/** The correct options (choice kinds only — empty for text / numeric). */
function correctOptionLabels(a: ReviewAnswer): OptionView[] {
  return [...a.options]
    .sort((x, y) => x.position - y.position)
    .filter((o) => o.isCorrect)
    .map((o) => ({ label: o.label, imageObjectId: imageObjectIdOf(o, 'imageObjectId') }));
}

function formatDateTime(d: Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

/** "41s" — whole seconds from ms; sub-second times render "<1s", never "0s". */
function formatElapsed(elapsedMs: number): string {
  const seconds = Math.round(elapsedMs / 1000);
  return seconds < 1 ? '<1s' : `${seconds}s`;
}

/** Per-verdict tallies over the review answers — the donut's segments. */
function countVerdicts(answers: ReviewAnswer[]): Record<Verdict, number> {
  const counts: Record<Verdict, number> = { correct: 0, incorrect: 0, pending: 0, ungraded: 0 };
  for (const a of answers) counts[verdictOf(a)] += 1;
  return counts;
}

/**
 * Mean of the timed answers' `elapsedMs` in seconds, or null when nothing
 * is timed — the time-strip's dashed reference line.
 */
function meanSecondsOf(answers: ReviewAnswer[]): number | null {
  const timed = answers.filter(
    (a): a is ReviewAnswer & { elapsedMs: number } => a.elapsedMs != null,
  );
  if (timed.length === 0) return null;
  return timed.reduce((sum, a) => sum + a.elapsedMs, 0) / timed.length / 1000;
}

/**
 * The review's slowest question (1-based display index + its time), or null
 * when no answer carries `elapsedMs` — SCH-AI-2b's hero pacing line.
 */
function slowestOf(answers: ReviewAnswer[]): { questionNumber: number; elapsedMs: number } | null {
  let slowest: { questionNumber: number; elapsedMs: number } | null = null;
  answers.forEach((a, i) => {
    if (a.elapsedMs != null && (slowest === null || a.elapsedMs > slowest.elapsedMs)) {
      slowest = { questionNumber: i + 1, elapsedMs: a.elapsedMs };
    }
  });
  return slowest;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ResultPage(): React.JSX.Element {
  return (
    <RequireAuth>
      <LearnShell title="Result">
        <ResultContent />
      </LearnShell>
    </RequireAuth>
  );
}

function ResultContent(): React.JSX.Element {
  const { attemptId } = Route.useParams();
  const attemptQ = trpc.learning.attempts.get.useQuery({ attemptId });
  const reviewQ = trpc.learning.attempts.getReview.useQuery({ attemptId });

  if (attemptQ.isPending) {
    return <ResultSkeleton />;
  }

  if (attemptQ.isError) {
    return (
      <ErrorCard
        message="We couldn't load this result. Please check your connection and try again."
        onRetry={() => {
          void attemptQ.refetch();
          void reviewQ.refetch();
        }}
      />
    );
  }

  const attempt = attemptQ.data;
  if (!attempt) {
    return (
      <EmptyState
        icon={FileQuestion}
        title="Result not found"
        description="This attempt doesn't exist or belongs to another account."
        action={
          <Button asChild>
            <Link to="/catalog">Back to courses</Link>
          </Button>
        }
      />
    );
  }

  const reviewAnswers = reviewQ.data?.answers ?? null;
  const hasPendingManual =
    attempt.status === 'submitted' ||
    (reviewAnswers?.some((a) => verdictOf(a) === 'pending') ?? false);
  const slowest = reviewAnswers ? slowestOf(reviewAnswers) : null;

  return (
    <div className="ls-stagger mx-auto flex max-w-4xl flex-col gap-4">
      <ScoreHero attempt={attempt} answers={reviewAnswers} slowest={slowest} />

      {attempt.status === 'submitted' || attempt.status === 'graded' ? (
        <div className="flex flex-col gap-2">
          <RecapCard attemptId={attemptId} />
          <StuckAskMentor quizId={attempt.quizId} />
        </div>
      ) : null}

      <SkillBreakdownCard answers={reviewAnswers} />

      {hasPendingManual ? (
        <div
          role="status"
          data-testid="pending-banner"
          className="animate-fade-in flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm text-foreground"
        >
          <Info className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
          <p>Some answers are graded by an instructor — your score may change.</p>
        </div>
      ) : null}

      <ReviewSection
        attemptId={attemptId}
        answers={reviewAnswers}
        isPending={reviewQ.isPending}
        isError={reviewQ.isError}
        onRetry={() => void reviewQ.refetch()}
      />

      {/* Action row — sticky to the viewport bottom (same treatment as the quiz
          runner's footer nav) so "Back to course" stays reachable without
          scrolling past ~30 review cards. One row at every width: at 390px the
          two short labels fit inside 358px of content box, and a single 56px
          band hides far less of the review than a stacked pair would. */}
      <div className="sticky bottom-0 z-30 -mx-4 flex items-center justify-between gap-3 border-t bg-background/80 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <Button variant="outline" className="press min-h-touch" asChild>
          <Link to="/quiz/$quizId" params={{ quizId: attempt.quizId }}>
            <RotateCcw className="size-4" aria-hidden="true" />
            Try again
          </Link>
        </Button>
        <BackToCourseButton quizId={attempt.quizId} courseId={attempt.courseId} />
      </div>
    </div>
  );
}

/**
 * The primary "Back" action on a result (Ishraf v1 QA: a result should return
 * to the COURSE it belongs to, not the whole catalog). Resolves the quiz's
 * course via the shared `useCourseIdForQuiz` probe; while unresolved (or for an
 * unenrolled/lapsed quiz) it falls back to the catalog so the button always
 * leads somewhere sensible.
 */
function BackToCourseButton({
  quizId,
  courseId: directCourseId,
}: {
  quizId: string;
  /** The quiz's course straight from `attempts.get` — preferred (works for
   *  practice runs); the probe is only a fallback for older cached attempts. */
  courseId?: string | null;
}): React.JSX.Element {
  const probed = useCourseIdForQuiz(quizId);
  const courseId = directCourseId ?? probed;
  // Same testid on both branches — the button is always present (only its
  // destination differs), so a viewport assertion never depends on the probe.
  if (courseId == null) {
    return (
      <Button className="press min-h-touch" data-testid="result-back-to-course" asChild>
        <Link to="/catalog">Back to courses</Link>
      </Button>
    );
  }
  return (
    <Button className="press min-h-touch" data-testid="result-back-to-course" asChild>
      <Link to="/course/$courseId" params={{ courseId }}>
        Back to course
      </Link>
    </Button>
  );
}

// ---------------------------------------------------------------------------
// "Stuck? Ask your mentor" (SCH-AI-4)
// ---------------------------------------------------------------------------

/**
 * Resolve which course `quizId` belongs to WITHOUT changing the api:
 * `learning.attempts.get` carries only `quizId`, and no learner-readable
 * quiz-by-id proc exists — so walk the caller's enrolled courses (the same
 * `courses.catalog` query `/catalog` uses) and probe each course's
 * `quizzes.listForLearner` (cache-shared with the course page) until one
 * contains the quiz. Bounded by the learner's enrollment count (consumer
 * LMS — typically 1-3 courses) and every probe is a cached read after the
 * first visit. Unresolvable (unenrolled / error) ⇒ null ⇒ button hidden.
 */
function useCourseIdForQuiz(quizId: string): string | null {
  const catalogQ = trpc.learning.courses.catalog.useQuery();
  const utils = trpc.useUtils();
  const [courseId, setCourseId] = React.useState<string | null>(null);
  const enrolledIds = React.useMemo(
    () => (catalogQ.data?.courses ?? []).filter((c) => c.enrolledAlready).map((c) => c.id),
    [catalogQ.data],
  );

  React.useEffect(() => {
    if (courseId != null || enrolledIds.length === 0) return;
    let cancelled = false;
    void (async () => {
      for (const id of enrolledIds) {
        try {
          const res = await utils.learning.quizzes.listForLearner.fetch({ courseId: id });
          if (res.quizzes.some((q) => q.id === quizId)) {
            if (!cancelled) setCourseId(id);
            return;
          }
        } catch {
          // FORBIDDEN (enrollment lapsed) etc. — skip this course, keep probing.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, enrolledIds, quizId, utils]);

  return courseId;
}

/**
 * The ghost entry under the recap card into the course-scoped mentor dock.
 * Renders nothing until the quiz's course is resolved (and never when it
 * can't be — see `useCourseIdForQuiz`).
 */
function StuckAskMentor({ quizId }: { quizId: string }): React.JSX.Element | null {
  const courseId = useCourseIdForQuiz(quizId);
  const [open, setOpen] = React.useState(false);
  if (courseId == null) return null;

  return (
    <div className="flex justify-end">
      <Button
        variant="ghost"
        size="sm"
        className="press"
        data-testid="mentor-open"
        onClick={() => setOpen(true)}
      >
        <MessageCircle className="size-4" aria-hidden="true" />
        Stuck? Ask your mentor
      </Button>
      <MentorDock open={open} courseId={courseId} onClose={() => setOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero score card
// ---------------------------------------------------------------------------

function ScoreHero({
  attempt,
  answers,
  slowest,
}: {
  attempt: Attempt;
  /**
   * Review answers when loaded — the donut's verdict segments. While they
   * are pending (or failed), the ring falls back to plain score-vs-rest.
   */
  answers: ReviewAnswer[] | null;
  /** SCH-AI-2b — present only when some review answer carries `elapsedMs`. */
  slowest: { questionNumber: number; elapsedMs: number } | null;
}): React.JSX.Element {
  const scorePercent = attempt.scorePercent;
  const centerLabel = scorePercent != null ? formatScorePercent(scorePercent) : '—';
  const counts = answers ? countVerdicts(answers) : null;

  // Verdict ring when the review is in; a plain score ring while it loads
  // (or failed to load) — never a misleading multi-color ring without data.
  const segments = counts
    ? [
        {
          key: 'correct',
          label: 'Correct',
          value: counts.correct,
          colorVar: '--color-primary',
          valueLabel: String(counts.correct),
        },
        {
          key: 'incorrect',
          label: 'Incorrect',
          value: counts.incorrect,
          colorVar: '--color-destructive',
        },
        {
          key: 'pending',
          label: 'Pending review',
          value: counts.pending,
          colorVar: '--color-warning',
        },
        {
          key: 'ungraded',
          label: 'Not graded',
          value: counts.ungraded,
          colorVar: '--color-muted-foreground',
        },
      ].filter((s) => s.value > 0)
    : scorePercent != null
      ? [
          { key: 'score', label: 'Score', value: scorePercent },
          {
            key: 'rest',
            label: 'Remaining',
            value: 100 - scorePercent,
            colorVar: '--color-muted-foreground',
          },
        ]
      : [];

  return (
    <Card className="animate-scale-in overflow-hidden">
      {/* Hero: score ring + meta column (the prototype's two-up hero) */}
      <CardContent className="grid grid-cols-1 items-center gap-6 px-6 py-8 sm:grid-cols-[auto_1fr] sm:gap-10 sm:py-10">
        <div className="animate-count-up mx-auto sm:mx-0">
          <DonutChart
            data-testid="score-donut"
            segments={segments}
            centerLabel={centerLabel}
            centerSubLabel={
              attempt.scoreRaw != null && attempt.scoreMax != null
                ? `${attempt.scoreRaw} / ${attempt.scoreMax} pts`
                : undefined
            }
            size={160}
            showLegend={false}
            ariaLabel={
              counts
                ? `Score ${centerLabel}: ${counts.correct} correct, ${counts.incorrect} incorrect, ${counts.pending} pending`
                : `Score ${centerLabel}`
            }
          />
        </div>

        <div className="flex min-w-0 flex-col items-center gap-3 text-center sm:items-start sm:text-left">
          <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Test result
          </span>

          <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
            {attempt.passed === true ? (
              <Badge variant="success" data-testid="pass-badge">
                <CheckCircle2 className="size-3.5" aria-hidden="true" />
                Passed
              </Badge>
            ) : attempt.passed === false ? (
              <Badge variant="destructive" data-testid="fail-badge">
                <XCircle className="size-3.5" aria-hidden="true" />
                Not passed
              </Badge>
            ) : null}

            {attempt.submittedAt ? (
              <Badge variant="outline">
                <Calendar className="size-3.5" aria-hidden="true" />
                Submitted {formatDateTime(attempt.submittedAt)}
              </Badge>
            ) : null}
          </div>

          {scorePercent == null ? (
            <p className="text-sm text-muted-foreground">This attempt hasn't been submitted yet.</p>
          ) : null}

          {slowest ? (
            <p data-testid="slowest-line" className="text-xs text-muted-foreground">
              Slowest: Q{slowest.questionNumber} ({formatElapsed(slowest.elapsedMs)})
            </p>
          ) : null}
        </div>
      </CardContent>

      {/* Stat strip — real verdict tallies (only once the review has loaded) */}
      {counts ? (
        <>
          <Separator />
          <dl className="grid grid-cols-2 sm:grid-cols-4">
            <HeroStat
              icon={<CheckCircle2 className="size-4 text-primary" aria-hidden="true" />}
              label="Correct"
              value={counts.correct}
              valueClassName="text-primary"
            />
            <HeroStat
              icon={<XCircle className="size-4 text-destructive" aria-hidden="true" />}
              label="Incorrect"
              value={counts.incorrect}
              valueClassName="text-destructive"
            />
            {counts.pending > 0 ? (
              <HeroStat
                icon={<Hourglass className="size-4 text-warning" aria-hidden="true" />}
                label="Pending"
                value={counts.pending}
                valueClassName="text-warning"
              />
            ) : (
              <HeroStat
                icon={<FileQuestion className="size-4 text-muted-foreground" aria-hidden="true" />}
                label="Questions"
                value={counts.correct + counts.incorrect + counts.pending + counts.ungraded}
              />
            )}
            <HeroStat
              icon={
                attempt.scoreRaw != null && attempt.scoreMax != null ? (
                  <CheckCircle2 className="size-4 text-muted-foreground" aria-hidden="true" />
                ) : (
                  <FileQuestion className="size-4 text-muted-foreground" aria-hidden="true" />
                )
              }
              label="Points"
              value={
                attempt.scoreRaw != null && attempt.scoreMax != null
                  ? `${attempt.scoreRaw} / ${attempt.scoreMax}`
                  : centerLabel
              }
            />
          </dl>
        </>
      ) : null}
    </Card>
  );
}

/** One cell of the hero stat strip — icon + uppercase label over a big toned value. */
function HeroStat({
  icon,
  label,
  value,
  valueClassName,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1.5 border-t border-border/60 p-4 sm:border-t-0 sm:border-r sm:last:border-r-0">
      <dt className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
        {icon}
        {label}
      </dt>
      <dd className={cn('text-2xl font-bold tracking-tight tabular-nums', valueClassName)}>
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review list
// ---------------------------------------------------------------------------

function ReviewSection({
  attemptId,
  answers,
  isPending,
  isError,
  onRetry,
}: {
  attemptId: string;
  answers: ReviewAnswer[] | null;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <section aria-label="Answer review" className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">Review answers</h2>
        {answers ? (
          <span className="text-sm text-muted-foreground tabular-nums">
            {answers.length} question{answers.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>

      {isPending ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      ) : isError ? (
        <ErrorCard
          message="We couldn't load the question breakdown. Your score above is unaffected."
          onRetry={onRetry}
        />
      ) : !answers || answers.length === 0 ? (
        <EmptyState
          size="compact"
          title="Nothing to review yet"
          description="The question breakdown appears here once your attempt is submitted."
        />
      ) : (
        <>
          <TimePerQuestionStrip answers={answers} />
          <ol className="ls-stagger flex flex-col gap-3">
            {answers.map((a, i) => (
              <ReviewRow key={a.questionId} attemptId={attemptId} answer={a} index={i} />
            ))}
          </ol>
        </>
      )}
    </section>
  );
}

/**
 * "Where your time went" — one column per question (height = seconds spent,
 * tone = verdict), the dashed rule at the learner's own average, and each
 * column a button deep-linking to its review row. Renders only when at
 * least 3 answers carry `elapsedMs` — never a chart over no data.
 */
function TimePerQuestionStrip({ answers }: { answers: ReviewAnswer[] }): React.JSX.Element | null {
  const timedCount = answers.filter((a) => a.elapsedMs != null).length;
  const meanSeconds = meanSecondsOf(answers);
  if (timedCount < 3 || meanSeconds == null) return null;

  const items = answers.map((a, i) => {
    const v = verdictOf(a);
    return {
      key: a.questionId,
      value: (a.elapsedMs ?? 0) / 1000,
      tone:
        v === 'correct'
          ? ('primary' as const)
          : v === 'incorrect'
            ? ('destructive' as const)
            : ('muted' as const),
      label: `Q${i + 1} — ${a.elapsedMs != null ? formatElapsed(a.elapsedMs) : 'untimed'}, ${v}`,
      targetId: `review-q${i + 1}`,
    };
  });

  return (
    <div data-testid="time-strip" className="rounded-xl border bg-card p-4 shadow-sm">
      <h3 className="mb-3 text-xs font-medium tracking-wide text-muted-foreground uppercase">
        Where your time went
      </h3>
      <ColumnChart
        items={items}
        max={Math.max(...items.map((i) => i.value))}
        referenceLine={{ value: meanSeconds, label: `avg ${Math.round(meanSeconds)}s` }}
        startLabel="Q1"
        endLabel={`Q${answers.length}`}
        height={72}
        ariaLabel={`Time per question, tallest is the slowest; dashed line marks your ${Math.round(meanSeconds)}s average`}
      />
    </div>
  );
}

function ReviewRow({
  attemptId,
  answer,
  index,
}: {
  attemptId: string;
  answer: ReviewAnswer;
  index: number;
}): React.JSX.Element {
  const verdict = verdictOf(answer);
  const learnerLabels = learnerOptionLabels(answer);
  const learnerText = learnerLabels == null ? formatLearnerText(answer) : null;
  const hasLearnerAnswer = learnerLabels != null || learnerText != null;
  const corrects = verdict === 'incorrect' ? correctOptionLabels(answer) : [];

  return (
    <li
      id={`review-q${index + 1}`}
      tabIndex={-1}
      data-testid="review-row"
      className="card-lift scroll-mt-20 list-none rounded-xl border bg-card p-4 shadow-sm sm:p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <VerdictIcon verdict={verdict} />
          <div className="min-w-0">
            <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              Question {index + 1}
            </p>
            {answer.passage ? (
              <div className="mt-1 mb-1.5 whitespace-pre-line rounded-md border-l-4 border-primary/40 bg-muted/40 p-2.5 text-xs leading-relaxed text-foreground">
                <MathText text={answer.passage} />
              </div>
            ) : null}
            <div className="mt-0.5 text-sm font-medium text-foreground">
              <MathText text={answer.prompt} />
            </div>
            <QuestionImage
              objectId={imageObjectIdOf(answer, 'promptImageObjectId')}
              alt={`Figure for question ${index + 1}`}
              className="mt-2"
            />
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span
            className={cn(
              'rounded-full border px-2.5 py-1 text-xs font-medium tabular-nums',
              verdict === 'correct'
                ? 'border-primary/30 bg-primary/10 text-primary'
                : verdict === 'incorrect'
                  ? 'border-destructive/25 bg-destructive/10 text-destructive'
                  : 'border-border bg-muted text-muted-foreground',
            )}
          >
            {verdict === 'pending' ? '—' : (answer.pointsAwarded ?? 0)} / {answer.points} pts
          </span>
          {answer.elapsedMs != null ? (
            <span
              data-testid="time-chip"
              className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium tabular-nums text-muted-foreground"
            >
              {formatElapsed(answer.elapsedMs)}
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2 pl-8 text-sm">
        <div className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="text-muted-foreground">Your answer: </span>
          {hasLearnerAnswer ? (
            learnerLabels != null ? (
              <MathLabelList options={learnerLabels} className="text-foreground" />
            ) : (
              <span className="break-words text-foreground">
                <MathText text={learnerText ?? ''} />
              </span>
            )
          ) : (
            <span className="text-muted-foreground italic">No answer</span>
          )}
        </div>

        {verdict === 'pending' ? (
          <Badge variant="warning" className="self-start">
            <Hourglass className="size-3.5" aria-hidden="true" />
            Pending review
          </Badge>
        ) : null}

        {corrects.length > 0 ? (
          <div className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="text-muted-foreground">
              Correct answer{corrects.length === 1 ? '' : 's'}:{' '}
            </span>
            <MathLabelList options={corrects} className="font-medium text-primary" />
          </div>
        ) : null}

        {verdict === 'incorrect' && answer.explanation ? (
          <div className="flex items-start gap-2 rounded-lg bg-primary/5 p-3 text-sm text-muted-foreground">
            <Lightbulb className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
            <p>
              <span className="font-medium text-foreground">Explanation: </span>
              <MathText text={answer.explanation} />
            </p>
          </div>
        ) : null}

        {verdict === 'incorrect' ? (
          // SCH-AI-3 — below the static author explanation, which always
          // stays visible regardless of quota / AI availability. Renders null
          // where the tenant's AI tutor is off (#209); the gate lives in the
          // component so every future mount inherits it.
          <ExplainMistake attemptId={attemptId} questionId={answer.questionId} />
        ) : null}
      </div>
    </li>
  );
}

/**
 * A comma-separated list of option labels, each rendered through <MathText> so
 * any LaTeX in a choice label displays. Inline-flowing (baseline-aligned) so it
 * reads as "A, B, C" alongside the leading "Your answer:" label. When an option
 * carries an image (`imageObjectId`, P-SCH-*) it renders as a small thumbnail
 * after that option's label — defensive: no image id ⇒ label only.
 */
function MathLabelList({
  options,
  className,
}: {
  options: OptionView[];
  className?: string;
}): React.JSX.Element {
  return (
    <span className={cn('inline-flex flex-wrap items-baseline gap-x-1', className)}>
      {options.map((option, i) => (
        <span key={i} className="inline-flex items-baseline gap-1">
          <MathText text={option.label} />
          <QuestionImage
            objectId={option.imageObjectId}
            alt={`Image for answer choice "${option.label}"`}
            className="inline-block max-h-16 align-middle"
          />
          {i < options.length - 1 ? <span aria-hidden="true">,</span> : null}
        </span>
      ))}
    </span>
  );
}

function VerdictIcon({ verdict }: { verdict: Verdict }): React.JSX.Element {
  if (verdict === 'correct') {
    return (
      <span
        title="Correct"
        className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10"
      >
        <CheckCircle2 className="size-5 text-primary" />
        <span className="sr-only">Correct</span>
      </span>
    );
  }
  if (verdict === 'incorrect') {
    return (
      <span
        title="Incorrect"
        className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-destructive/10"
      >
        <XCircle className="size-5 text-destructive" />
        <span className="sr-only">Incorrect</span>
      </span>
    );
  }
  if (verdict === 'pending') {
    return (
      <span
        title="Pending review"
        className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-warning/10"
      >
        <Hourglass className="size-5 text-warning" />
        <span className="sr-only">Pending review</span>
      </span>
    );
  }
  return (
    <span
      title="Not graded"
      className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted"
    >
      <span
        className="block size-4 rounded-full border-2 border-muted-foreground/40"
        aria-hidden="true"
      />
      <span className="sr-only">Not graded</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Loading / error affordances
// ---------------------------------------------------------------------------

function ResultSkeleton(): React.JSX.Element {
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4">
      <Card className="overflow-hidden">
        <CardContent className="grid grid-cols-1 items-center gap-6 px-6 py-8 sm:grid-cols-[auto_1fr] sm:gap-10 sm:py-10">
          <Skeleton className="mx-auto size-40 rounded-full sm:mx-0" />
          <div className="flex flex-col items-center gap-3 sm:items-start">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-6 w-32 rounded-full" />
            <Skeleton className="h-4 w-40" />
          </div>
        </CardContent>
        <Separator />
        <div className="grid grid-cols-2 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex flex-col gap-1.5 border-t border-border/60 p-4 sm:border-t-0 sm:border-r sm:last:border-r-0"
            >
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-7 w-12" />
            </div>
          ))}
        </div>
      </Card>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-6 w-32" />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-28 w-full rounded-xl" />
        ))}
      </div>
    </div>
  );
}

function ErrorCard({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <Card className="animate-scale-in">
      <CardContent className="flex flex-col items-center gap-3 px-6 py-8 text-center">
        <XCircle className="size-6 text-rose-500" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">{message}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/catalog">Back to courses</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
